import { defineApp, matchRoutes } from 'umi'
import { createTracking } from './services/DataService'
import { collectClientInfo, collectAppInfo, collectUserInfo } from './utils/trackingHelper'

/**
 * 从路由配置中获取路由名称
 */
function getRouteName(clientRoutes, pathname) {
  try {
    const matches = matchRoutes(clientRoutes, pathname)
    if (matches && matches.length > 0) {
      const route = matches[matches.length - 1]?.route
      return route?.name || route?.path || null
    }
  } catch (e) {
    // 忽略错误
  }
  return null
}

/**
 * 统一的路由处理逻辑
 */
function handleRouteTracking({ location, clientRoutes, action, basename, isFirst }) {
  // 延迟执行以等待页面标题更新 (例如等待 PageContainer 或 Helmet 生效)
  setTimeout(() => {
    try {
      // 收集用户信息
      const user = collectUserInfo()
    
    if (!user) {
      return
    }

    // 收集客户端信息
    const client = collectClientInfo()

    // 获取路由名称
    const routeName = getRouteName(clientRoutes, location.pathname)

    // 收集应用信息
    const app = collectAppInfo(location, document, routeName)

    // 构建完整的埋点数据
    const trackingData = {
      client,
      app,
      user,
      event: {
        name: 'page_view',
        type: 'navigation'
      },
      data: {
        action: action || 'PUSH',
        basename: basename || '',
        is_first: isFirst || false,
        route_path: location.pathname,
        route_name: routeName,
        route_search: location.search || '',
        route_hash: location.hash || '',
        route_state: location.state || null,
        route_key: location.key || null,
        timestamp: new Date().toISOString()
      }
    }

    // 发送埋点
    createTracking(trackingData).catch(error => {
      // 静默处理错误
    })
  } catch (error) {
    // 静默处理错误
  }
  }, 300)
}

/**
 * 错误去重缓存 (防止同一错误短时间内重复上报)
 */
const errorCache = new Map()
const ERROR_CACHE_DURATION = 5000 // 5秒内相同错误只上报一次

/**
 * 生成错误唯一标识
 */
function getErrorKey(error, event) {
  const message = error?.message || event?.message || 'Unknown error'
  const filename = event?.filename || ''
  const lineno = event?.lineno || 0
  const colno = event?.colno || 0
  return `${message}|${filename}|${lineno}|${colno}`
}

/**
 * 全局异常报错埋点
 */
function handleGlobalError({ error, event }) {
  try {
    // 错误去重检查
    const errorKey = getErrorKey(error, event)
    const now = Date.now()
    const lastReportTime = errorCache.get(errorKey)
    
    if (lastReportTime && now - lastReportTime < ERROR_CACHE_DURATION) {
      return // 跳过重复错误
    }
    
    errorCache.set(errorKey, now)
    
    // 定期清理过期缓存
    if (errorCache.size > 100) {
      const expireTime = now - ERROR_CACHE_DURATION
      for (const [key, time] of errorCache.entries()) {
        if (time < expireTime) {
          errorCache.delete(key)
        }
      }
    }

    // 收集用户信息
    const user = collectUserInfo()
    
    if (!user) {
      return
    }

    // 收集客户端信息
    const client = collectClientInfo()

    // 收集应用信息
    const app = collectAppInfo(window.location, document)

    // 构建错误信息
    const errorInfo = {
      message: error?.message || event?.message || 'Unknown error',
      name: error?.name || 'Error',
      stack: error?.stack || '',
      filename: event?.filename || '',
      lineno: event?.lineno || 0,
      colno: event?.colno || 0,
      error_type: error?.constructor?.name || 'Error',
      timestamp: new Date().toISOString()
    }

    // 构建完整的埋点数据
    const trackingData = {
      client,
      app,
      user,
      event: {
        name: 'global_error',
        type: 'error'
      },
      data: errorInfo
    }

    // 发送埋点
    createTracking(trackingData).catch(err => {
      // 静默处理错误
    })
  } catch (e) {
    // 静默处理错误
  }
}

/**
 * 初始化全局错误监听
 */
function initErrorTracking() {
  // 监听 JavaScript 运行时错误
  window.addEventListener('error', (event) => {
    // 过滤资源加载错误 (如图片、脚本加载失败)
    if (event.target !== window) {
      return
    }
    
    handleGlobalError({
      error: event.error,
      event: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    })
  }, true) // 使用捕获阶段

  // 监听未处理的 Promise 拒绝
  window.addEventListener('unhandledrejection', (event) => {
    // 阻止默认的控制台错误输出 (可选)
    // event.preventDefault()
    
    handleGlobalError({
      error: event.reason,
      event: {
        message: event.reason?.message || String(event.reason) || 'Unhandled Promise Rejection',
        filename: '',
        lineno: 0,
        colno: 0
      }
    })
  })
}

// 初始化全局错误监听
initErrorTracking()

// Umi 4 推荐使用 defineApp
export default defineApp({
  onRouteChange: handleRouteTracking
})

// 保留命名导出以兼容某些 Umi 配置
export const onRouteChange = handleRouteTracking;
